// Per-material undo/redo stack for the MaterialInspector.
//
// Lives in module-level state (not React) so it survives Inspector unmount /
// remount as the user switches between assets in the content browser. Each
// entry is a deep-cloned snapshot of `material_assets.data` immediately
// *before* an edit happened — Ctrl+Z restores that snapshot.
//
// Coalescing: consecutive edits to the same key within COALESCE_MS are
// treated as one gesture (a slider drag from 0 → 0.8 is one undo step, not
// hundreds). The pre-gesture snapshot is preserved; later ticks no-op.

const COALESCE_MS = 1000
const MAX_DEPTH = 50

const _historyByAsset = new Map()
// entry: { stack: data[], redoStack: data[], lastKey: string|null, lastTime: number }

const _listeners = new Set()
function _notify() { _listeners.forEach(fn => { try { fn() } catch {} }) }

function _get(assetId) {
  let h = _historyByAsset.get(assetId)
  if (!h) {
    h = { stack: [], redoStack: [], lastKey: null, lastTime: 0 }
    _historyByAsset.set(assetId, h)
  }
  return h
}

// Record a snapshot of `prevData` taken immediately before mutating field
// `key`. Returns the entry so the inspector can decide whether anything
// changed. Idempotent on consecutive same-key edits within COALESCE_MS.
export function recordEdit(assetId, prevData, key) {
  if (!assetId) return
  const h = _get(assetId)
  const now = Date.now()
  const isContinuationOfGesture =
    h.lastKey === key && (now - h.lastTime) < COALESCE_MS && h.stack.length > 0
  if (!isContinuationOfGesture) {
    h.stack.push(_clone(prevData))
    if (h.stack.length > MAX_DEPTH) h.stack.shift()
    h.redoStack = []   // any new edit invalidates the redo branch
  }
  h.lastKey = key
  h.lastTime = now
  _notify()
}

// Pop the last snapshot. Caller passes the *current* data so it can be
// pushed onto redoStack. Returns the snapshot to restore, or null if there
// is nothing to undo.
export function undo(assetId, currentData) {
  const h = _historyByAsset.get(assetId)
  if (!h || h.stack.length === 0) return null
  const restore = h.stack.pop()
  h.redoStack.push(_clone(currentData))
  if (h.redoStack.length > MAX_DEPTH) h.redoStack.shift()
  h.lastKey = null   // reset coalescing so next edit pushes fresh
  h.lastTime = 0
  _notify()
  return restore
}

export function redo(assetId, currentData) {
  const h = _historyByAsset.get(assetId)
  if (!h || h.redoStack.length === 0) return null
  const restore = h.redoStack.pop()
  h.stack.push(_clone(currentData))
  if (h.stack.length > MAX_DEPTH) h.stack.shift()
  h.lastKey = null
  h.lastTime = 0
  _notify()
  return restore
}

export function canUndo(assetId) {
  const h = _historyByAsset.get(assetId)
  return !!h && h.stack.length > 0
}

export function canRedo(assetId) {
  const h = _historyByAsset.get(assetId)
  return !!h && h.redoStack.length > 0
}

export function clearHistory(assetId) {
  _historyByAsset.delete(assetId)
  _notify()
}

// Subscribe to any change in the history (pushes, undos, redos, clears).
// React components call this to re-render the Undo / Redo buttons.
export function subscribe(fn) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

function _clone(o) {
  // Shallow clone is sufficient — material_assets.data is a flat record of
  // scalars, color arrays, UUIDs, and a couple of [u,v] tuples. We do clone
  // those small arrays so popping a snapshot can't leak mutation back.
  if (!o || typeof o !== 'object') return o
  const out = {}
  for (const k of Object.keys(o)) {
    const v = o[k]
    out[k] = Array.isArray(v) ? [...v] : v
  }
  return out
}
